import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ReactComponent as ImageIcon } from '../../assets/icons-redesign/plus.svg';
import plus from '../../assets/icons-redesign/plus.svg';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/notification/notification.slice';
import { useUploadCSVMutation, useGetSurveyQuery } from '../../redux/survey/survey.endpoints';
import ErrorLogService from '../../services/error-log/error-log.service';

interface CSVUploadProps {
  extSurveyId?: string;
  initiallyOpen?: boolean;
}

const CSVUpload: React.FC<CSVUploadProps> = ({ extSurveyId, initiallyOpen = false }) => {
  const [isVisible, setIsVisible] = useState(initiallyOpen);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const { data: survey } = useGetSurveyQuery(
    { ext_survey_id: extSurveyId || '' },
    { skip: !extSurveyId }
  );
  const isDisabled = survey?.questions?.some(q => q.title!=="Untitled_Question" || q.answers.length > 0) ?? false;

  const [uploadCSV] = useUploadCSVMutation();

  const handleUpload = async(file: File) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      dispatch(showNotification({
        summary: 'Please upload a valid CSV file',
        sticky: true,
        theme: 'warning',
      }));
      return;
    }

    if (!extSurveyId) {
      dispatch(showNotification({
        summary: 'Survey ID is required',
        sticky: true,
        theme: 'warning',
      }));
      return;
    }

    try {
      await uploadCSV({ ext_survey_id: extSurveyId, file }).unwrap();
      dispatch(showNotification({
        summary: 'CSV uploaded successfully',
        sticky: false,
        theme: 'success',
      }));
    } catch (error) {
      ErrorLogService.logError(error ?? 'CSV upload error', 'csv-upload', 'handleUpload');
    } finally {
      setIsVisible(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const renderUploadArea = () => {
    return (
      <div
        className={`flex flex-col items-center justify-center h-64 w-full border-2 border-dashed rounded-lg
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          transition-colors duration-200 cursor-pointer`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <ImageIcon className="size-8 mb-4 fill-gray-401" />
        <p className="text-gray-401 mb-2">
          Drag and drop your CSV file here
        </p>
        <p className="text-gray-401 text-sm flex items-center gap-1">
          <span>
            or
          </span>
          <span className="text-gray-401 underline">
            browse files
          </span>
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleUpload(file);
            }
          }}
        />
      </div>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsVisible(true)}
        disabled={isDisabled}
        className={`ml-2 flex items-center rounded-3xl bg-white lg-max:p-2 px-6 py-2 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-501 ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''}`}
      >
        <img
          className="mr-2 lg-max:m-0"
          src={plus}
          alt=""
          draggable={false}
        />
        <span className="lg-max:hidden">
          Upload CSV
        </span>
      </button>
      <Dialog
        visible={isVisible}
        onHide={() => setIsVisible(false)}
        header="CSV Survey Builder"
        className="w-[500px] p-4"
        draggable={false}
        blockScroll
      >
        <div className="mt-8">
          {renderUploadArea()}
        </div>
      </Dialog>
    </>
  );
};

export default CSVUpload;